import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import {
  MaritalStatusEnumType,
  TaxFilingStatusEnumType,
} from "@libs/api/patients";
import { EligibilityScreenLayout } from "@/blocks/EligibilityScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import {
  useCurrentHouseholdMemberQuery,
  useGetHouseholdMemberQuery,
} from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import { maritalStatusOptions } from "@/domain/eligibility";
import { handlePossessiveApostrophe } from "@/utils/string";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

const OPTIONS_REQUIRES_NEW_HHM: MaritalStatusEnumType[] = [
  "MARRIED",
  "COMMON_LAW",
];

const TAX_FILING_STATUS_PER_MARITAL_STATUS: Record<
  MaritalStatusEnumType,
  TaxFilingStatusEnumType
> = {
  DIVORCED: "HEAD",
  NEVER_MARRIED: "HEAD",
  SEPARATED: "HEAD",
  WIDOWED: "WIDOWER",
  MARRIED: "JOINTLY",
  COMMON_LAW: "JOINTLY",
  REGISTERED_DOMESTIC_PARTNER: "HEAD",
};

export const EligibilityMaritalStatusScreen = () => {
  const router = useRouter();

  const { supporterFirstName, supporterHouseholdMemberId } =
    useLocalSearchParams<{
      supporterFirstName: string;
      supporterHouseholdMemberId: string;
    }>();

  const {
    mutate: updateHouseholdMember,
    isPending: isUpdatingHouseholdMember,
  } = useUpdateHouseholdMemberMutation();
  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: supporterHouseholdMember } = useGetHouseholdMemberQuery(
    supporterHouseholdMemberId,
  );
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);
  const [selectedMaritalStatus, setSelectedMaritalStatus] = useState<
    MaritalStatusEnumType | undefined
  >(undefined);

  if (!currentHouseholdMember?.id) {
    return <VnLoadingSpinner />;
  }

  const handleNextStep = (option: MaritalStatusEnumType) => {
    if (OPTIONS_REQUIRES_NEW_HHM.includes(option)) {
      setIsAddingNewHouseholdMember(true);

      return;
    }

    return goToNextStep();
  };

  const goToNextStep = () => {
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "MARITAL_STATUS",
        },
      },
      {
        onSuccess: () => {
          router.navigate({
            pathname: "/eligibility/live-care-children",
            params: {
              supporterFirstName,
              supporterHouseholdMemberId,
            },
          });
        },
      },
    );
  };

  const onPress = (option: MaritalStatusEnumType) => {
    setSelectedMaritalStatus(option);

    updateHouseholdMember(
      {
        id: supporterHouseholdMemberId ?? currentHouseholdMember.id,
        householdMemberData: {
          maritalStatus: option,
          taxFilingStatus: TAX_FILING_STATUS_PER_MARITAL_STATUS[option],
        },
      },
      {
        onSuccess: () => handleNextStep(option),
      },
    );
  };

  if (isAddingNewHouseholdMember && selectedMaritalStatus) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={
          supporterHouseholdMemberId
            ? [
                "PARENT",
                "GRANDPARENT",
                "SIBLING",
                "CHILD",
                "STEPCHILD",
                "GRANDCHILD",
                "FOSTER_CHILD",
                "OTHER",
              ]
            : ["SPOUSE", "PARTNER"]
        }
        ageRangeOptions={["YOUNG_ADULT", "ADULT", "TURNING_SENIOR", "SENIOR"]}
        shouldShowAddAnotherButton={false}
        assumptions={{
          taxFilingStatus: "JOINTLY",
          childCaregiverStatus: supporterHouseholdMemberId
            ? supporterHouseholdMember?.childCaregiverStatus
            : currentHouseholdMember.childCaregiverStatus,
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: supporterHouseholdMemberId
              ? "DEPENDENT_OF_SPOUSE"
              : "FILES_JOINTLY_WITH",
          },
          sameMailingAddress: true,
          maritalStatus: selectedMaritalStatus,
        }}
        titleI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityMaritalStatusScreen.supporterFormTitle"
            : "native:eligibilityMaritalStatusScreen.formTitle"
        }
        descriptionI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityMaritalStatusScreen.supporterFormDescription"
            : "native:eligibilityMaritalStatusScreen.formDescription"
        }
        titleI18nOptions={{
          supporterFirstName: handlePossessiveApostrophe(supporterFirstName),
        }}
        descriptionI18nOptions={{
          supporterFirstName: handlePossessiveApostrophe(supporterFirstName),
        }}
        onSuccessOrContinue={goToNextStep}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  return (
    <EligibilityScreenLayout
      shouldShowBackButton={false}
      titleI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilityMaritalStatusScreen.supporterTitle"
          : "native:eligibilityMaritalStatusScreen.title"
      }
      descriptionI18nKey={
        supporterHouseholdMemberId
          ? undefined
          : "native:eligibilityMaritalStatusScreen.description"
      }
      titleI18nOptions={{
        supporterFirstName: handlePossessiveApostrophe(supporterFirstName),
      }}>
      <VnBox className="gap-3">
        {maritalStatusOptions.map(option => (
          <VnButton
            key={option.value}
            onPress={() => onPress(option.value)}
            isLoading={
              (isUpdatingHousehold || isUpdatingHouseholdMember) &&
              selectedMaritalStatus === option.value
            }
            variant="outline"
            className="border-border justify-start rounded-md">
            <VnText variant="lg">{option.label}</VnText>
          </VnButton>
        ))}
      </VnBox>
    </EligibilityScreenLayout>
  );
};
